import axios from '../axios'

/*
 * Quota-cascade voor Bestelling plaatsen.
 *
 * Hierarchie: eindklant > partner > kassa. Per type (coins/notes) wint het eerste
 * niet-lege niveau (heel niveau per type; losse coupures worden niet gemengd).
 *
 * "Leeg" wordt ALTIJD afgeleid uit min/max (alle rijen min 0 én max 0), voor álle
 * niveaus. De backend-vlaggen empty_coins/empty_notes worden bewust genegeerd: ze waren
 * inconsistent (omgekeerd op kassa, eerder afwezig op partner). Min/max = bron van waarheid.
 *
 * De drie endpoints hebben elk een andere (wisselende) body-vorm; collectDenom/normalize
 * brengen ze naar één vorm.
 */

type LevelName = 'customer' | 'partner' | 'kassa'

export type QuotaLevel = {
  level: LevelName
  empty_coins: boolean
  empty_notes: boolean
  ordercoins: boolean
  ordernotes: boolean
  announcecoins: boolean
  announcenotes: boolean
  orderperwafel: boolean
  coins: Coin[]
  notes: Coin[]
}

const lc = (r: any): any => {
  if (!r || typeof r !== 'object') return r
  const out: any = {}
  for (const k in r) out[k.toLowerCase()] = r[k]
  return out
}

const mapRow = (raw: any): Coin => {
  const r = lc(raw)
  return {
    partnerid: 0,
    id: Number(r.apicustomerdenomid ?? r.apipartnerdenomid ?? r.apikassadenomid ?? r.id ?? 0),
    coupure: Number(r.coupure),
    minqty: Number(r.minqty ?? 0),
    maxqty: Number(r.maxqty ?? 0),
    perqty: Number(r.perquantity ?? r.perqty ?? 0),
  }
}

const allZero = (rows: Coin[]) => rows.every(r => r.minqty === 0 && r.maxqty === 0)

// Verzamel coins/notes/settings uit de verschillende (inconsistente) body-vormen:
//  - bare array van { denom: {coins|notes|settings} }   (nieuw partner)
//  - { object: [ { denom: {...} }, ... ] }               (oud partner)
//  - { object: { coins, notes, ... } }                   (kassa)
//  - { coins, notes, ... }                               (eindklant, object direct)
const collectDenom = (data: any): { coins: any[]; notes: any[]; settings: any } | null => {
  const payload = data?.object !== undefined ? data.object : data

  // Array van losse denom-fragmenten (elk één coupure of de settings) → samenvoegen.
  if (Array.isArray(payload)) {
    const coins: any[] = []
    const notes: any[] = []
    let settings: any = {}
    for (const el of payload) {
      const d = (el && el.denom) ? el.denom : el
      if (!d || typeof d !== 'object') continue
      if (Array.isArray(d.coins)) coins.push(...d.coins)
      if (Array.isArray(d.notes)) notes.push(...d.notes)
      if (d.ordercoins !== undefined || d.ordernotes !== undefined || d.announcecoins !== undefined || d.announcenotes !== undefined) {
        settings = { ...settings, ...d }
      }
    }
    return { coins, notes, settings }
  }

  // Eén object: ofwel direct coins/notes, ofwel genest onder .denom.
  if (payload && typeof payload === 'object') {
    const d = (Array.isArray(payload.coins) || Array.isArray(payload.notes)) ? payload
            : (Array.isArray(payload.denom?.coins) || Array.isArray(payload.denom?.notes)) ? payload.denom
            : null
    if (d) return { coins: Array.isArray(d.coins) ? d.coins : [], notes: Array.isArray(d.notes) ? d.notes : [], settings: d }
  }

  return null
}

export const normalize = (level: LevelName, data: any): QuotaLevel | null => {
  const collected = collectDenom(data)
  if (!collected) return null
  const s = lc(collected.settings)

  const coins = collected.coins.map(mapRow).filter(c => c.coupure !== 0).sort((a, b) => b.coupure - a.coupure)
  const notes = collected.notes.map(mapRow).filter(c => c.coupure !== 0).sort((a, b) => b.coupure - a.coupure)
  const settingsRow = lc([...collected.coins, ...collected.notes].find(r => Number(lc(r).coupure) === 0))

  // "Leeg" = alle rijen min 0 én max 0. We leiden dit altijd zelf af uit min/max:
  // de backend-vlaggen empty_coins/empty_notes zijn onbetrouwbaar (omgekeerd op kassa, afwezig op partner).
  return {
    level,
    empty_coins: allZero(coins),
    empty_notes: allZero(notes),
    ordercoins: !!s.ordercoins,
    ordernotes: !!s.ordernotes,
    announcecoins: !!s.announcecoins,
    announcenotes: !!s.announcenotes,
    orderperwafel: !!(s.orderperwafel ?? settingsRow?.orderperwafel),
    coins,
    notes,
  }
}

const fetchCustomerLevel = async (auth: Auth, customerid: number): Promise<QuotaLevel | null> => {
  const { data } = await axios.get('customer/denom/search', { params: { token: auth.data?.token, customerid } })
  console.log(`[SERVER RESPONSE: customer/denom/search] customerid=${customerid}`, data)
  return normalize('customer', data)
}

const fetchPartnerLevel = async (auth: Auth, partner: string): Promise<QuotaLevel | null> => {
  const { data } = await axios.post('partner/denom/search', { token: auth.data?.token, partner })
  console.log(`[SERVER RESPONSE: partner/denom/search] partner=${partner}`, data)
  return normalize('partner', data)
}

// Eén kassa met haar denom-niveau. `cn` bepaalt het type (munt/biljet).
export type KassaQuota = { kassaid: number; name: string; cn: string; level: QuotaLevel }

export type OrderQuota = {
  customer: QuotaLevel | null
  partner: QuotaLevel | null
  kassas: KassaQuota[]
}

// Haalt alle relevante niveaus op: eindklant, partner en kassa (één gecombineerde fetch).
// De kassaids en het maximaal 2 subtabs (coins + notes) worden afgeleid uit de response-rijen;
// de kassa-lijst wordt enkel voor naam-opzoek gebruikt.
export const fetchOrderQuota = async (
  auth: Auth,
  opts: { customerid: number; partner: string; kassas: Kassa[] }
): Promise<OrderQuota> => {
  const kassas = opts.kassas || []

  const fetchKassa = async () => {
    const { data } = await axios.get('kassa/denom/search', { params: { token: auth.data?.token, eindklantnr: opts.customerid } })
    console.log(`[SERVER RESPONSE: kassa/denom/search] eindklantnr=${opts.customerid}`, data)
    return data
  }

  const [customer, partner, kassaRaw] = await Promise.all([
    fetchCustomerLevel(auth, opts.customerid).catch(() => null),
    fetchPartnerLevel(auth, opts.partner).catch(() => null),
    fetchKassa().catch(() => null),
  ])

  const kassaLevel = kassaRaw ? normalize('kassa', kassaRaw) : null

  const kassaQuotas: KassaQuota[] = []
  if (kassaLevel) {
    const obj = kassaRaw?.object ?? kassaRaw
    const kassaidFrom = (rows: any[]): number | null =>
      Array.isArray(rows) ? (Number(rows.find((r: any) => r.kassaid)?.kassaid) || null) : null
    const getName = (kassaid: number) =>
      kassas.find(k => k.kassaid === kassaid)?.omschrijving || String(kassaid)

    const coinKassaId = kassaidFrom(obj?.coins ?? [])
    const noteKassaId = kassaidFrom(obj?.notes ?? [])
    if (coinKassaId) kassaQuotas.push({ kassaid: coinKassaId, name: getName(coinKassaId), cn: 'C', level: kassaLevel })
    if (noteKassaId) kassaQuotas.push({ kassaid: noteKassaId, name: getName(noteKassaId), cn: 'N', level: kassaLevel })
  }

  return { customer, partner, kassas: kassaQuotas }
}

export type CascadeResult = { rows: Coin[]; level: LevelName | null; found: boolean }

const isEmptyFor = (lvl: QuotaLevel | null, type: 'c' | 'n') =>
  !lvl || (type === 'c' ? lvl.empty_coins : lvl.empty_notes)

// Kassa's van een type (cn matcht).
export const kassasOfType = (quota: OrderQuota, type: 'c' | 'n'): KassaQuota[] =>
  quota.kassas.filter(k => (k.cn || '').toLowerCase().includes(type))

// Pure cascade voor één type: eerste niet-lege niveau (eindklant > partner > kassa) wint.
export const pickCascade = (quota: OrderQuota, type: 'c' | 'n'): CascadeResult => {
  const rowsOf = (lvl: QuotaLevel) => (type === 'c' ? lvl.coins : lvl.notes)

  const candidates = kassasOfType(quota, type)
  const kassaLevel: QuotaLevel | null =
    (candidates.find(k => !isEmptyFor(k.level, type))?.level) ?? (candidates[0]?.level) ?? null

  const ordered: QuotaLevel[] = [quota.customer, quota.partner, kassaLevel]
    .filter((l): l is QuotaLevel => !!l)

  for (const lvl of ordered) {
    if (!isEmptyFor(lvl, type)) return { rows: rowsOf(lvl), level: lvl.level, found: true }
  }
  return { rows: [], level: null, found: false }
}
